import axios from "axios";
import { create } from "zustand";

// Create axios instance with interceptors
const authAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Add request interceptor to include auth token
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  const tenantId = localStorage.getItem("tenantId");
  const role = localStorage.getItem("role");
  console.log("Request token:", token); // Debug token
  console.log("Request tenantId:", tenantId); // Debug tenantId
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId) {
    config.params = config.params || {};
    config.params.tenantId = tenantId;
    if (role === "SuperAdmin") {
      config.params.allTenants = true; // Optional: Adjust based on UI
    }
  }
  return config;
});

// Add response interceptor to handle 401 errors
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized error (e.g., redirect to login)
      console.error("Authentication failed - redirecting to login");
    }
    return Promise.reject(error);
  }
);

export const useDashboardStore = create((set, get) => ({
  dashboardData: {
    roadAssessed: { totalRoadsAssessed: 0 },
    assetsTagged: { assetsTagged: 0 },
    pendingMaintenance: { pendingMaintenance: 0 },
    overallConditionRating: { overallConditionRating: 0 },
    roadConditionAssessment: {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      normal: 0,
    },
    averageConditionPerMonth: { data: [] },
    mapSegments: [],
  },
  isLoading: false,
  error: null,
  lastUpdated: null,

  fetchDashboardData: async (month = "january", year = "2025") => {
    const tenantId = localStorage.getItem("tenantId");
    const isSuperAdmin = localStorage.getItem("role") === "SuperAdmin";
    set({ isLoading: true, error: null });
    try {
      const params = {};
      if (tenantId && tenantId !== "null" && !isSuperAdmin) {
        params.tenantId = tenantId; // Only include tenantId for non-SuperAdmins with valid tenantId
      }
      if (isSuperAdmin) {
        params.allTenants = true; // Use allTenants for SuperAdmins
      }
      const responses = await Promise.all([
        authAxios.get("api/dashboard/total-roads-assessed", { params }),
        authAxios.get("api/dashboard/assets-tagged", { params }),
        authAxios.get("api/dashboard/pending-maintenance", { params }),
        authAxios.get("api/dashboard/overall-condition-rating", { params }),
        authAxios.get(
          `api/dashboard/road-condition-assessment?month=${month.toLowerCase()}`, { params }
        ),
        authAxios.get("api/dashboard/map-segments", { params }),
      ]);

      // Fetch averageConditionPerMonth separately based on year
    const averageConditionResponse = await authAxios.get(
      `api/dashboard/average-condition-per-month?year=${year}`,
      { params }
    );

      set({
        dashboardData: {
          roadAssessed: responses[0].data,
          assetsTagged: responses[1].data,
          pendingMaintenance: responses[2].data,
          overallConditionRating: responses[3].data,
          roadConditionAssessment: responses[4].data,
          averageConditionPerMonth: {
            data: averageConditionResponse.data.data || averageConditionResponse.data || [],
          },
          mapSegments: responses[5].data.mapSegments || responses[5].data,
        },
        isLoading: false,
        lastUpdated: new Date().toISOString(),
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch dashboard data";

      set({
        isLoading: false,
        error: errorMessage,
      });
      console.error("Dashboard fetch error:", error);
    }
  },

  updateDashboardData: async () => {
    const tenantId = localStorage.getItem("tenantId");
    const isSuperAdmin = localStorage.getItem("role") === "SuperAdmin";
    set({ isLoading: true, error: null });
    try {
      const params = {};
      if (tenantId && tenantId !== "null" && !isSuperAdmin) {
        params.tenantId = tenantId; // Only include tenantId for non-SuperAdmins with valid tenantId
      }
      if (isSuperAdmin) {
        params.allTenants = true; // Use allTenants for SuperAdmins
      }
      const response = await authAxios.post("api/dashboard/update", {}, { params });

      if (response.status === 403) {
        throw new Error("You don't have permission to update dashboard data");
      }

      console.log("Dashboard updated:", response.data);
      set({ isLoading: false });

      // Refresh data after update
      get().fetchDashboardData();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update dashboard";

      set({
        isLoading: false,
        error: errorMessage,
      });

      console.error("Dashboard update error:", error);
    }
  },
}));

export default useDashboardStore;
